/**
 * B2cSellerNoLocation — the seller line the msite wishlist size sheet shows when
 * there is no delivery location yet (`b2cSellerConfig.isEnabled`).
 *
 * With no pincode / address, `fetchDeliveryDetailsV2` stops at its
 * `hasVisibleDeliveryLocation` guard, so there is no scopedView response and no
 * SLA cards to render — `DeliverySLAList` returns null on empty, which takes its
 * seller row with it. The design still names a seller for the picked size, off
 * the catalogue (wishlist `variantOptions[].sellerName`), with a note that the
 * real one depends on where it ships to.
 *
 * Deliberately not routed through DeliverySLAList: there is no view model here,
 * and making that component render on empty options would change the PDP and
 * cart too. The seller row markup and classes are the card's, so the two states
 * look alike.
 */
import React, { FC } from 'react';
import classNames from 'classnames/bind';
import { useSelector } from 'react-redux';
import Text from '../../atoms/Text';
import properties from '../../../modules/ajio-util/properties';
import { getB2cSellerConfig } from '../../selectors/featureFlagsSelector';
import styles from './index.pcss';

const DEFAULT_SELLER_CHANGE_NOTE = 'Seller may change based on address/pin code selected';

const cx = classNames.bind(styles);

const imageBaseUrl = properties.getProperty('imageBaseUrl');
/** Storefront glyph for the seller row, one per store theme. */
const SELLER_ICON = `${imageBaseUrl}/static/img/b2c-seller-icon.svg`;
const SELLER_ICON_LUXE = `${imageBaseUrl}/static/img/b2c-seller-icon-luxe.svg`;
const SELLER_ICON_FLEEK = `${imageBaseUrl}/static/img/b2c-seller-icon-fleek.svg`;

/** Ajiogram has its own glyph and wins over luxe, matching the row's colours. */
const sellerIconFor = (luxe: boolean, fleek: boolean): string => {
  if (fleek) return SELLER_ICON_FLEEK;
  return luxe ? SELLER_ICON_LUXE : SELLER_ICON;
};

interface B2cSellerNoLocationProps {
  /** Catalogue seller for the selected size. Nothing renders without one. */
  sellerName?: string;
  /** luxe theme, forwarded from the host sheet */
  luxe?: boolean;
  /** Ajiogram (fleek) store theme */
  fleek?: boolean;
  /** Size / bag / wishlist drawers — same fleek-dialog tint as the B2C card. */
  onDialogSurface?: boolean;
}

const B2cSellerNoLocation: FC<B2cSellerNoLocationProps> = ({
  sellerName,
  luxe = false,
  fleek = false,
  onDialogSurface = false
}) => {
  const b2cSellerConfig = useSelector((state: any) => getB2cSellerConfig(state)) || {};
  const note = b2cSellerConfig?.sellerChangeNote || DEFAULT_SELLER_CHANGE_NOTE;

  if (!sellerName) return null;

  return (
    // The theme classes carry the seller row's background, so they come along.
    <div
      className={cx('sla-list', 'sla-list--b2c', 'sla-list--seller-only', {
        'sla-list--luxe': luxe,
        'sla-list--fleek': fleek,
        'sla-list--fleek-dialog': fleek && onDialogSurface
      })}
    >
      <div className={cx('sla-seller')}>
        <img
          className={cx('sla-seller__icon')}
          src={sellerIconFor(luxe, fleek)}
          alt=""
          aria-hidden="true"
        />
        <Text variant="T2" color={fleek ? 'White' : luxe ? 'Light_Black' : 'Nero'} removeGrouping>
          {`Seller: ${sellerName}`}
        </Text>
      </div>
      <div className={cx('sla-list__footnote')}>
        <Text variant="T2" color={fleek ? 'White' : 'Dark_Grey'}>
          {note.startsWith('*') ? note : `*${note}`}
        </Text>
      </div>
    </div>
  );
};

export default B2cSellerNoLocation;
